import React, { useRef, useState } from 'react';
import { Icon } from './icons';
import { Segmented } from './shared';
import { toKey } from './dataAdapters';
import { Modal } from './sheets';
import UserAvatar from '../components/UserAvatar';

// Editing commits on blur/Enter rather than per-keystroke: this writes to
// Firebase, and a keystroke-per-write would fire a request for every digit.
function FloorInput({ value, onChange }) {
  const [draft, setDraft] = useState(String(value ?? 0));
  const [editing, setEditing] = useState(false);

  // Track external changes (another device, initial load) unless mid-edit.
  if (!editing && draft !== String(value ?? 0)) setDraft(String(value ?? 0));

  const commit = () => {
    setEditing(false);
    const parsed = parseFloat(draft);
    const next = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    setDraft(String(next));
    if (next !== value) onChange(next);
  };

  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
      onClick={(e) => e.stopPropagation()}
    >
      <span style={{ fontSize: 13, color: 'var(--ink-3)', fontWeight: 600 }}>$</span>
      <input
        value={draft}
        inputMode="decimal"
        aria-label="Low balance alert threshold"
        onFocus={() => setEditing(true)}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') { setDraft(String(value ?? 0)); e.currentTarget.blur(); }
        }}
        style={{
          width: 72, height: 32, padding: '0 8px', textAlign: 'right',
          borderRadius: 8, border: '1px solid var(--line)',
          background: 'var(--surface-2)', color: 'var(--ink)',
          fontSize: 13, fontWeight: 600,
        }}
      />
    </span>
  );
}

function SettingsRow({ icon, label, sub, value, right, onClick, danger }) {
  return (
    <button
      onClick={onClick}
      className="pressable"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 13,
        width: '100%',
        textAlign: 'left',
        padding: '13px 8px',
        margin: '0 -8px',
        borderRadius: 6,
      }}
    >
      {icon && (
        <span style={{ width: 20, flexShrink: 0, display: 'flex', color: danger ? 'var(--alarm)' : 'var(--ink-3)' }}>
          <Icon name={icon} size={17} />
        </span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14.5, fontWeight: 500, color: danger ? 'var(--alarm)' : 'var(--ink)' }}>{label}</div>
        {sub && <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 2 }}>{sub}</div>}
      </div>
      {value && <span style={{ fontSize: 13.5, color: 'var(--ink-3)', fontWeight: 500 }}>{value}</span>}
      {right !== undefined ? right : onClick && !danger && <Icon name="chevR" size={18} style={{ color: 'var(--ink-3)' }} />}
    </button>
  );
}

function SettingsGroup({ label, children }) {
  const kids = React.Children.toArray(children);
  return (
    <div className="pg-section">
      {label && <div className="eyebrow">{label}</div>}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {kids.map((k, i) => (
          <div key={i} style={{ borderBottom: i < kids.length - 1 ? '1px solid var(--line-soft)' : 'none' }}>
            {k}
          </div>
        ))}
      </div>
    </div>
  );
}

function ProfileHeader({
  user,
  compact,
  profilePhotoUrl,
  uploadingProfilePhoto,
  onUploadProfilePhoto,
  onRemoveProfilePhoto,
}) {
  const fileInputRef = useRef(null);
  const name = user?.displayName || user?.email?.split('@')[0] || 'User';
  const avatarSize = compact ? 52 : 62;
  const hasCustomPhoto = !!profilePhotoUrl;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file && onUploadProfilePhoto) onUploadProfilePhoto(file);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <UserAvatar user={user} profilePhotoUrl={profilePhotoUrl} size={avatarSize} fontSize={24} />
        {uploadingProfilePhoto && (
          <span
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: 999,
              background: 'rgba(0,0,0,0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="refresh" size={22} className="spin" style={{ color: '#fff' }} />
          </span>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="title" style={{ fontSize: 18, fontWeight: 600, color: 'var(--ink)' }}>{name}</div>
        <div style={{ fontSize: 13.5, color: 'var(--ink-3)' }}>{user?.email}</div>
        {onUploadProfilePhoto && (
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingProfilePhoto}
              className="pressable chip"
              style={{ fontSize: 12, opacity: uploadingProfilePhoto ? 0.5 : 1 }}
            >
              <Icon name="upload" size={14} style={{ marginRight: 4 }} />
              {hasCustomPhoto ? 'Change photo' : 'Upload photo'}
            </button>
            {hasCustomPhoto && onRemoveProfilePhoto && (
              <button
                type="button"
                onClick={onRemoveProfilePhoto}
                disabled={uploadingProfilePhoto}
                className="pressable chip"
                style={{ fontSize: 12, color: 'var(--neg)', opacity: uploadingProfilePhoto ? 0.5 : 1 }}
              >
                Remove
              </button>
            )}
          </div>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        aria-hidden
      />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="field-label">{title}</div>
      <div>{children}</div>
    </div>
  );
}

export function FocusSettings({
  theme,
  setTheme,
  runwayFloor,
  onRunwayFloorChange,
  onCats,
  onBudgets,
  isDesktop,
  user,
  profilePhotoUrl,
  uploadingProfilePhoto,
  onUploadProfilePhoto,
  onRemoveProfilePhoto,
  budgetCount,
  categoryCount,
  onInstall,
  showInstall,
  onSignOut,
  onReCategorize,
  recategorizingAI,
  uncategorizedCount,
  showTips = false,
  onToggleShowTips,
}) {
  const budgetedCount = budgetCount;
  const [showPrivacy, setShowPrivacy] = useState(false);

  return (
    <div className="pg" style={{ maxWidth: isDesktop ? 680 : 'none' }}>
      <ProfileHeader
        user={user}
        profilePhotoUrl={profilePhotoUrl}
        uploadingProfilePhoto={uploadingProfilePhoto}
        onUploadProfilePhoto={onUploadProfilePhoto}
        onRemoveProfilePhoto={onRemoveProfilePhoto}
      />
      <SettingsGroup label="Preferences">
        <SettingsRow
          icon="moon"
          label="Appearance"
          sub={theme === 'dark' ? 'Dark' : theme === 'light' ? 'Light' : 'System'}
          right={<Segmented size="sm" options={[{ value: 'system', label: 'System' }, { value: 'light', label: 'Light' }, { value: 'dark', label: 'Dark' }]} value={theme} onChange={setTheme} />}
        />
        <SettingsRow
          icon="flag"
          label="Show tips"
          sub="Display the getting-started card on the dashboard"
          right={
            <button
              type="button"
              role="switch"
              aria-checked={showTips}
              onClick={(e) => { e.stopPropagation(); onToggleShowTips && onToggleShowTips(!showTips); }}
              className="pressable"
              style={{
                width: 44, height: 26, borderRadius: 99, flexShrink: 0,
                background: showTips ? 'var(--ink)' : 'transparent',
                border: `1px solid ${showTips ? 'var(--ink)' : 'var(--line-strong)'}`,
                position: 'relative', transition: 'background 0.18s, border-color 0.18s',
              }}
            >
              <span style={{
                position: 'absolute', top: 2, left: showTips ? 20 : 2,
                width: 20, height: 20, borderRadius: 99,
                background: showTips ? 'var(--bg)' : 'var(--ink-3)',
                transition: 'left 0.18s, background 0.18s',
              }} />
            </button>
          }
        />
      </SettingsGroup>

      <SettingsGroup label="Money">
        <SettingsRow icon="tag" label="Categories" sub={`${categoryCount} categories`} onClick={onCats} />
        <SettingsRow icon="plan" label="Budgets" sub={`${budgetedCount} active`} onClick={onBudgets} />
        {onRunwayFloorChange && (
          <SettingsRow
            icon="shield"
            label="Low balance alert"
            sub="Warn before projected cash drops below this"
            right={<FloorInput value={runwayFloor} onChange={onRunwayFloorChange} />}
          />
        )}
      </SettingsGroup>

      <SettingsGroup label="App">
        {onReCategorize && (
          <SettingsRow
            icon="tag"
            label="Auto-categorize"
            sub={
              recategorizingAI
                ? 'Categorizing…'
                : uncategorizedCount > 0
                ? `${uncategorizedCount} uncategorized transaction${uncategorizedCount !== 1 ? 's' : ''}`
                : 'All transactions categorized'
            }
            right={
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onReCategorize(); }}
                disabled={recategorizingAI || uncategorizedCount === 0}
                className="pressable chip"
                style={{ fontSize: 12, opacity: (recategorizingAI || uncategorizedCount === 0) ? 0.45 : 1 }}
              >
                {recategorizingAI ? '…' : 'Run'}
              </button>
            }
          />
        )}
        {showInstall && (
          <SettingsRow icon="arrowDn" label="Install app" sub="Add to home screen" onClick={onInstall} />
        )}
        <SettingsRow icon="shield" label="Privacy & security" onClick={() => setShowPrivacy(true)} />
      </SettingsGroup>

      <SettingsGroup>
        <SettingsRow icon="logout" label="Sign out" danger onClick={onSignOut} />
      </SettingsGroup>
      <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--ink-3)', padding: '6px 0 4px' }}>Prisma</div>

      <Modal open={showPrivacy} onClose={() => setShowPrivacy(false)} title="Privacy & Security">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.55 }}>
          <Section title="Your data">
            All transactions, budgets, and categories are stored in your personal Firebase Realtime Database account. No one else can read or write your data — access is enforced by Firebase security rules tied to your user ID.
          </Section>
          <Section title="Who can access this app">
            Prisma is a private app. Sign-in is restricted to a pre-approved list of accounts managed in Firebase. Anyone not on that list is automatically signed out.
          </Section>
          <Section title="Bank connections">
            Bank account connections are powered by Plaid. Your bank credentials are entered only in Plaid’s secure interface and are never seen or stored by this app — Plaid provides read-only access to your accounts and transaction history.
          </Section>
          <Section title="Third-party services">
            Transaction auto-categorization uses the Google Gemini API. Only transaction descriptions and amounts are sent — no personal details or account numbers.
          </Section>
          <Section title="No tracking">
            This app does not use analytics, advertising, or any third-party tracking. Vercel Speed Insights is enabled to monitor page load performance only.
          </Section>
        </div>
      </Modal>
    </div>
  );
}

export function budgetCountFromEnvelopes(envelopes, categories) {
  return categories.filter((c) => envelopes[toKey(c)] !== undefined && envelopes[toKey(c)] > 0).length;
}
